export interface TimelineEvent {
  year: number;
  name: string;
  title: string;
  description: string;
  image?: string;
  category: 'philosophy' | 'chemistry' | 'physics' | 'quantum';
}

export interface Scientist {
  name: string;
  years: string;
  contribution: string;
  image?: string;
  nationality: string;
  nobelPrize?: string;
}

export interface AtomicModel {
  name: string;
  scientist: string;
  year: number;
  description: string;
  characteristics: string[];
  image?: string;
}

export const timeline: TimelineEvent[] = [
  {
    year: -400,
    name: "Démocrite",
    title: "L'atome, particule indivisible",
    description: "Le philosophe grec propose que toute matière est composée de petites particules insécables qu'il nomme 'atomos' (qui ne peut être coupé). Une intuition remarquable qui ne sera confirmée que 2200 ans plus tard.",
    category: 'philosophy'
  },
  {
    year: 1808,
    name: "John Dalton",
    title: "Premier modèle atomique scientifique",
    description: "Dalton énonce que chaque élément est composé d'atomes identiques et que les réactions chimiques sont des réarrangements d'atomes. Il établit les premières masses atomiques relatives.",
    category: 'chemistry'
  },
  {
    year: 1897,
    name: "J.J. Thomson",
    title: "Découverte de l'électron",
    description: "Thomson découvre l'électron grâce à ses expériences sur les rayons cathodiques. Il propose le modèle du 'plum pudding' où les électrons sont dispersés dans une sphère de charge positive.",
    category: 'physics'
  },
  {
    year: 1911,
    name: "Ernest Rutherford",
    title: "Découverte du noyau atomique",
    description: "Grâce à son expérience de bombardement d'une feuille d'or, Rutherford découvre que l'atome possède un noyau central très dense et positif, autour duquel gravitent les électrons.",
    category: 'physics'
  },
  {
    year: 1913,
    name: "Niels Bohr",
    title: "Modèle des orbites quantifiées",
    description: "Bohr introduit l'idée révolutionnaire que les électrons ne peuvent occuper que certaines orbites spécifiques autour du noyau, expliquant ainsi les spectres d'émission atomiques.",
    category: 'quantum'
  },
  {
    year: 1919,
    name: "Ernest Rutherford",
    title: "Découverte du proton",
    description: "Rutherford identifie le proton comme constituant du noyau atomique lors d'expériences de bombardement d'azote par des particules alpha.",
    category: 'physics'
  },
  {
    year: 1924,
    name: "Louis de Broglie",
    title: "Dualité onde-corpuscule",
    description: "De Broglie propose que les électrons, comme la lumière, possèdent à la fois des propriétés d'onde et de particule. Cette théorie jette les bases de la mécanique quantique.",
    category: 'quantum'
  },
  {
    year: 1926,
    name: "Erwin Schrödinger",
    title: "Équation d'onde de la mécanique quantique",
    description: "Schrödinger développe son équation d'onde qui décrit le comportement des électrons comme des nuages de probabilité plutôt que comme des particules sur des orbites définies.",
    category: 'quantum'
  },
  {
    year: 1927,
    name: "Werner Heisenberg",
    title: "Principe d'incertitude",
    description: "Heisenberg énonce son célèbre principe : on ne peut connaître simultanément avec précision la position et la vitesse d'une particule. Une révolution conceptuelle fondamentale.",
    category: 'quantum'
  },
  {
    year: 1932,
    name: "James Chadwick",
    title: "Découverte du neutron",
    description: "Chadwick découvre le neutron, particule neutre du noyau atomique, complétant ainsi notre compréhension de la structure nucléaire.",
    category: 'physics'
  },
  {
    year: 1964,
    name: "Murray Gell-Mann",
    title: "Théorie des quarks",
    description: "Gell-Mann propose que les protons et neutrons sont eux-mêmes composés de particules encore plus fondamentales : les quarks.",
    category: 'quantum'
  },
  {
    year: 2012,
    name: "CERN",
    title: "Découverte du boson de Higgs",
    description: "Le CERN annonce la découverte du boson de Higgs, particule fondamentale responsable de la masse des autres particules. Une confirmation majeure du modèle standard.",
    category: 'quantum'
  }
];

export const scientists: Scientist[] = [
  {
    name: "John Dalton",
    years: "1766-1844",
    contribution: "Fondateur de la théorie atomique moderne. Il établit que les éléments sont composés d'atomes identiques.",
    nationality: "Britannique",
  },
  {
    name: "J.J. Thomson",
    years: "1856-1940",
    contribution: "Découvre l'électron et propose le premier modèle atomique avec charges.",
    nationality: "Britannique",
    nobelPrize: "Prix Nobel de Physique 1906"
  },
  {
    name: "Ernest Rutherford",
    years: "1871-1937",
    contribution: "Découvre le noyau atomique et le proton. Père de la physique nucléaire.",
    nationality: "Néo-Zélandais",
    nobelPrize: "Prix Nobel de Chimie 1908"
  },
  {
    name: "Niels Bohr",
    years: "1885-1962",
    contribution: "Développe le modèle des orbites quantifiées et contribue à la mécanique quantique.",
    nationality: "Danois",
    nobelPrize: "Prix Nobel de Physique 1922"
  },
  {
    name: "Louis de Broglie",
    years: "1892-1987",
    contribution: "Propose la dualité onde-corpuscule de la matière.",
    nationality: "Français",
    nobelPrize: "Prix Nobel de Physique 1929"
  },
  {
    name: "Erwin Schrödinger",
    years: "1887-1961",
    contribution: "Développe l'équation d'onde fondamentale de la mécanique quantique.",
    nationality: "Autrichien",
    nobelPrize: "Prix Nobel de Physique 1933"
  },
  {
    name: "Werner Heisenberg",
    years: "1901-1976",
    contribution: "Formule le principe d'incertitude et développe la mécanique matricielle.",
    nationality: "Allemand",
    nobelPrize: "Prix Nobel de Physique 1932"
  },
  {
    name: "James Chadwick",
    years: "1891-1974",
    contribution: "Découvre le neutron, complétant notre compréhension du noyau atomique.",
    nationality: "Britannique",
    nobelPrize: "Prix Nobel de Physique 1935"
  },
  {
    name: "Marie Curie",
    years: "1867-1934",
    contribution: "Pionnière de la radioactivité, découvre le polonium et le radium.",
    nationality: "Polonaise/Française",
    nobelPrize: "Prix Nobel de Physique 1903, Chimie 1911"
  }
];

export const atomicModels: AtomicModel[] = [
  {
    name: "Modèle de Dalton",
    scientist: "John Dalton",
    year: 1808,
    description: "L'atome est une sphère solide, indivisible et indestructible. Tous les atomes d'un même élément sont identiques.",
    characteristics: [
      "Atome = sphère pleine",
      "Indivisible",
      "Identique pour un élément donné",
      "Pas de structure interne"
    ]
  },
  {
    name: "Modèle de Thomson",
    scientist: "J.J. Thomson",
    year: 1897,
    description: "Le modèle du 'plum pudding' : les électrons négatifs sont dispersés dans une sphère de charge positive, comme des raisins dans un pudding.",
    characteristics: [
      "Sphère de charge positive",
      "Électrons dispersés à l'intérieur",
      "Charge globale neutre",
      "Première inclusion des électrons"
    ]
  },
  {
    name: "Modèle de Rutherford",
    scientist: "Ernest Rutherford",
    year: 1911,
    description: "L'atome possède un noyau central très petit et dense contenant toute la charge positive, autour duquel gravitent les électrons.",
    characteristics: [
      "Noyau central dense et positif",
      "Électrons en orbite",
      "Atome majoritairement vide",
      "Comparable au système solaire"
    ]
  },
  {
    name: "Modèle de Bohr",
    scientist: "Niels Bohr",
    year: 1913,
    description: "Les électrons occupent des orbites circulaires spécifiques et quantifiées. Ils ne peuvent sauter que d'une orbite à une autre en absorbant ou émettant de l'énergie.",
    characteristics: [
      "Orbites circulaires quantifiées",
      "Niveaux d'énergie discrets",
      "Sauts quantiques d'électrons",
      "Explique les spectres atomiques"
    ]
  },
  {
    name: "Modèle quantique",
    scientist: "Schrödinger & Heisenberg",
    year: 1926,
    description: "Les électrons ne sont plus sur des orbites définies mais dans des nuages de probabilité (orbitales). On ne peut connaître que la probabilité de présence d'un électron.",
    characteristics: [
      "Orbitales = nuages de probabilité",
      "Principe d'incertitude",
      "Fonction d'onde",
      "Modèle actuel le plus précis"
    ]
  }
];

export const categoryColors = {
  philosophy: '#c792ea',
  chemistry: '#64ffda',
  physics: '#f78c6c',
  quantum: '#82aaff'
};

export const categoryNames = {
  philosophy: 'Philosophie',
  chemistry: 'Chimie',
  physics: 'Physique',
  quantum: 'Physique Quantique'
};
